import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import { CircularProgress, FormControl, InputLabel, MenuItem, Select, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import NegativeIconImgSrc from "../../assets/image/png/error.png";
import "./DeliveryInvoiceDetails.css";

interface CancelStatusModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (saleCondition: string, reason: string) => void;
  loading?: boolean;
}

const CancelStatusModal: React.FC<CancelStatusModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [saleCondition, setSaleCondition] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [error, setError] = useState<string>("");

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const reasonMap: { [key: string]: string[] } = ConfigDictionary?.salesReturnReasonsMap ?? {};
  const reasonArray: string[] = ConfigDictionary.salesReturnReasons ?? ["Expired", "Damaged", "Wrong Order", "Slow Moving", "Consumer Complaint"];

  // Get reasons based on selected sale condition or return default array
  const getAvailableReasons = () => {
    // If reasonMap is empty, use reasonArray directly
    if (Object.keys(reasonMap).length === 0) {
      return reasonArray;
    }
    // If reasonMap exists, use it based on sale condition
    if (!saleCondition) return [];
    return reasonMap[saleCondition] || [];
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setSaleCondition("");
      setSelectedReason("");
      setError("");
    }
  }, [open]);

  // Clear reason when sale condition changes
  useEffect(() => {
    setSelectedReason("");
    setError("");
  }, [saleCondition]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const handleSubmit = () => {
    setError("");

    // If reasonMap is empty, we don't need sale condition
    if (Object.keys(reasonMap).length > 0 && !saleCondition) {
      setError("Please select a sale condition");
      return;
    }

    if (!selectedReason) {
      setError("Please add reason for cancelling");
      return;
    }

    // For case without reasonMap, pass empty string as saleCondition
    const finalSaleCondition = Object.keys(reasonMap).length === 0 ? "" : saleCondition;
    
    
    onSubmit(finalSaleCondition, selectedReason);
  };

  const isSubmitDisabled = loading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="cancel-status-modal-title"
    >
      <div className="cancel-status-modal-container">
        <button 
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: "none",
            border: "none",
            cursor: "pointer"
          }}
          onClick={onClose}
        >
          <CloseIcon style={{ color: "#707070" }} />
        </button>

        <img src={NegativeIconImgSrc} className="cancel-status-modal-img" />
        <h3 className="cancel-status-modal-header">Cancel Item</h3>
        <p className="cancel-status-modal-subtext">Please provide cancellation details:</p>

        {Object.keys(reasonMap).length === 0 ? (
          /* Case 1: No Sale Condition Radio Buttons - Direct Reason Selection */
          <FormControl
            fullWidth
            size="small"
            style={{ marginTop: "16px", width: "80%" }}
          >
            <InputLabel id="cancel-reason-label">
              Select Reason
            </InputLabel>
            <Select
              labelId="cancel-reason-label"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              label="Select Reason"
              size="small"
            >
              {getAvailableReasons().map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          /* Case 2: With Sale Condition Radio Buttons */
          <>
            {/* Sale Condition Radio Buttons */}
            <div className="cancel-status-sale-condition">
              <h4>Sale Condition:</h4>
              <FormControl component="fieldset">
                <RadioGroup
                  value={saleCondition}
                  onChange={(e) => setSaleCondition(e.target.value)}
                  row
                >
                  {Object.keys(reasonMap).map((key) => (
                    <FormControlLabel 
                      key={key}
                      value={key} 
                      control={<Radio size="small" />} 
                      label={key} 
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </div>

            {/* Reason Selection */}
            <FormControl
              fullWidth
              size="small"
              disabled={!saleCondition}
              style={{ marginTop: "16px", width: "80%" }}
            >
              <InputLabel id="cancel-reason-label">
                Select Reason
              </InputLabel>
              <Select
                labelId="cancel-reason-label"
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                label="Select Reason"
                size="small"
              >
                {getAvailableReasons().map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {/* Error Message */}
        {error && (
          <p className="cancel-status-error">{error}</p>
        )}

        {/* Submit Button */}
        <button
          className="cancel-status-submit-btn"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          style={{
            marginTop: "20px",
            padding: "12px 24px",
            backgroundColor: isSubmitDisabled ? "#EBEBEB" : "#00C6B1",
            color: isSubmitDisabled ? "#7C7C7C" : "#FFFFFF",
            border: "none",
            borderRadius: "4px",
            cursor: isSubmitDisabled ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "600" as any
          }}
        >
          {loading ? <CircularProgress size={16} style={{ color: "white" }} /> : "Submit"}
        </button>
      </div>
    </Modal>
  );
};

export default CancelStatusModal;
